import { computed, inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { I18nService } from '@core/i18n-legacy';
import { InsightsDialogComponent } from '@core/impact-tracker-smart-ui';
import {
  formatNumberWithCommas,
  ImpactTrackerInsightsTranslationsMap,
  TellentImpactTrackerActions,
  TellentImpactTrackerSelectors,
  // eslint-disable-next-line boundaries/element-types
} from '@core/tellent-data'; // FIXME
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import { AppKind, injectTellentAppKind, TELLENT_SUITE_ENABLED } from '@core/tellent-types';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';
import { truncate } from '@core/utils-legacy';

@Injectable({ providedIn: 'root' })
export class ImpactTrackerService {
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);
  private readonly injector = inject(Injector);
  private readonly i18n = inject(I18nService);
  private readonly tellentSuiteEnabled = inject(TELLENT_SUITE_ENABLED);
  private readonly router = inject(Router);
  private readonly appKind = injectTellentAppKind();

  private readonly currentMembership = this.currentUserFacade.organizationMembership;

  public readonly data = this.store.selectSignal(
    TellentImpactTrackerSelectors.byKind('recruitment'),
  );

  public readonly canShowDialogTrigger = computed(() => this.data()?.insights.length! > 3);

  public readonly cards = computed(() => {
    const data = this.data();

    return data?.insights
      .filter(({ visible }) => visible)
      .map(({ name, values }) => {
        const map = runInInjectionContext(this.injector, ImpactTrackerInsightsTranslationsMap);
        const { icon, title, label, formatFns, tooltip } = map[name];

        const [value1, value2] = values;
        const [value1FormatFn, value2FormatFn] = formatFns;

        return {
          icon,
          value: value1FormatFn(value1),
          title: this.i18n.translate(title),
          label: this.i18n.translate(label, {
            number: value2FormatFn ? value2FormatFn(value2) : value2,
          }),
          tooltip: this.i18n.translate(tooltip),
        };
      });
  });

  public orgName = computed(() => truncate(this.currentMembership().org.name, 40));

  public total = computed(() => formatNumberWithCommas(this.data()?.total));

  public dialogData = computed(() => ({
    heading: this.i18n.translate('tlc.impact_tracker.metrics_banner.metrics_dialog.heading', {
      companyName: this.orgName(),
      link: this.i18n.translate('tlc.impact_tracker.metrics_banner.metrics_dialog.link'),
    }),
    subheading: this.i18n.translate('tlc.impact_tracker.metrics_banner.metrics_dialog.subheading'),
    cards: this.cards(),
    logoHeading: this.i18n.translate(
      'tlc.impact_tracker.metrics_banner.metrics_dialog.logo_heading',
    ),
  }));

  public openDialog(): void {
    if (this.appKind === AppKind.Recruitee) {
      this.router.navigate([{ outlets: { dialog: ['impact'] } }]);
    } else {
      this.dialogService.openCustom(
        InsightsDialogComponent,
        {
          width: DialogWidth.Large,
        },
        this.dialogData,
      );
    }
  }

  public fetchData(): void {
    if (this.tellentSuiteEnabled)
      this.store.dispatch(TellentImpactTrackerActions.fetchCollection());
  }
}
